import {
  DxButton,
  DxCard,
  DxCardContent,
  DxContainer,
  DxGrid,
  DxIcon,
  DxProcessHeader,
  DxText,
} from '@dvag/design-system-react';
import { PageCodes } from '@dvag/dfs-constant-config';
import i18next from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetFavorites, useUpdateFavorites } from '@dvag/dfs-ui-blocks/hooks';
import { getToken } from '@dvag/dfs-orchestrator-client';
import { navigatePrevious } from '@dvag/dfs-orchestrator-client/messengers';
import {
  FavoritePage,
  filterFavorites,
  useFavoritesAutosave,
  useFavoritesSearch,
} from '@dvag/dfs-ui-blocks/components';
import { checkIcon } from 'utils/util';
import { useGetFavoritesCatalog } from 'hooks/useGetFavoritesCatalog';
import { useGetPolicies } from 'hooks/useGetPolicies';
import { FavoritesCatalog } from './component/FavoritesCatalog/FavoritesCatalog';
import { ListsSeparator } from './component/ListsSeparator/ListsSeparator';
import SearchInput from './component/SearchInput/SearchInput';
import { UserFavorites } from './component/UserFavorites/UserFavorites';
import { useNotification } from '../../utils/notification/NotificationContext';
import { useContinuation } from '../../utils/useContinuation';
import { UnsavedOrPendingModal } from '../../component/UnsavedOrPendingModal';
import featureFlag from '../../service/featureFlag';
import './style.css';
import { useReadOnlyMode } from '../../hooks/useReadOnlyMode';

export const Favorites = () => {
  const isReadMode = useReadOnlyMode();
  const policiesQuery = useGetPolicies();
  const policies = policiesQuery.data?.policies;
  const pagesConfigOptions = useMemo(
    () => ({
      isFeatureOn: (feature: string) => !!featureFlag.gb?.isOn(feature),
      policies: policies ?? [],
    }),
    [policies],
  );
  const pagesQuery = useGetFavorites(getToken, true, true, pagesConfigOptions);
  const hasFavoriteAccess = useCallback(
    ({ permissions, featureFlags, policies: pagePolicies }: FavoritePage) =>
      (featureFlags?.every((featureFlagCode) => featureFlag.gb?.isOn(featureFlagCode)) ?? true) &&
      (permissions?.every((permission) => featureFlag.gb?.isOn(permission)) ?? true) &&
      (pagePolicies?.every((policy) => policies?.includes(policy) ?? false) ?? true),
    [policies],
  );

  const [favorites, setFavorites] = useState<FavoritePage[]>(pagesQuery.data);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const catalogFavoritesQuery = useGetFavoritesCatalog(true);
  const catalog = catalogFavoritesQuery.data ?? [];

  const { showError } = useNotification();
  const errorToast = (errorMessage: string) => {
    showError({ description: errorMessage });
  };

  const { continuation, getContinuationHandler, getCanContinueHandler, onContinuationRequest } =
    useContinuation();
  const onCanContinueChange = useMemo(() => getCanContinueHandler(), [getCanContinueHandler]);

  const updateFavorites = useUpdateFavorites(errorToast, getToken);

  const [favoritesCatalog, setFavoritesCatalog] = useState<FavoritePage[]>([]);

  const getAliases = (code: PageCodes) => catalog.find((p) => p.code === code)?.aliases || [];

  const onIsDirtyChange = (isDirtyParam: boolean) => {
    if (!isDirtyParam) {
      getCanContinueHandler();
    }
    onCanContinueChange(!isDirtyParam);
  };

  const { move, remove, append, getAllResources, flushChanges } = useFavoritesAutosave(
    favorites,
    updateFavorites.mutateAsync,
    (requestResponses) => {
      const deletedFavorites = requestResponses
        .filter((requestResponse) => requestResponse.action === 'DELETE')
        .map((a) => ({ ...a.request }));
      setFavoritesCatalog((currentFavorites) => [
        ...currentFavorites.map((fav) => {
          const deletedFavorite = deletedFavorites.find((df) => df.code === fav.code);
          if (deletedFavorite) {
            delete deletedFavorite.id;
            return deletedFavorite;
          }
          return fav;
        }),
      ]);
    },
    setIsSubmitting,
    onIsDirtyChange,
  );

  const { filteredFavorites, searchValue, setSearchValue } = useFavoritesSearch(
    getAllResources(),
    false,
    getAliases,
  );

  useEffect(() => {
    const hasFavorite = (code: string) =>
      pagesQuery.data.some((favorite) => code === favorite.code);
    setFavorites(pagesQuery.data);

    setFavoritesCatalog(
      catalogFavoritesQuery.data?.filter(
        (favorite) => !hasFavorite(favorite.code) && hasFavoriteAccess(favorite),
      ) || [],
    );
  }, [catalogFavoritesQuery.data, hasFavoriteAccess, pagesQuery.data]);

  const filteredFavoritesCatalog = filterFavorites(
    searchValue,
    false,
    favoritesCatalog,
    getAliases,
  );

  useEffect(() => {
    onContinuationRequest(flushChanges);
  }, [flushChanges, onContinuationRequest]);

  return (
    <>
      <DxProcessHeader
        headline={i18next.t('favorites.headline')}
        data-testid="favorites-headline"
      />
      <DxContainer type="page" color="transparent">
        <DxCard className="favorites-card">
          <DxCardContent>
            <DxContainer color="transparent" className="flex">
              <DxContainer color="transparent">
                <DxIcon
                  color="gray-83"
                  icon={checkIcon('information')}
                  size={24}
                  className="info-icon"
                />
              </DxContainer>
              <DxText type="ps">{i18next.t('favorites.information')}</DxText>
            </DxContainer>

            <DxGrid mq1="12/12/12" rowgap="24">
              <SearchInput
                searchValue={searchValue}
                isDisabled={isReadMode}
                onSearch={setSearchValue}
              />
              <DxContainer className="favorites-lists-container" color="transparent">
                <DxGrid
                  mq1="12/12/12"
                  mq2="12/12/12"
                  mq3="5-2-5"
                  mq4="5-2-5"
                  mq5="5-2-5"
                  className="content-grid"
                >
                  <UserFavorites
                    pages={filteredFavorites || []}
                    onItemMove={move}
                    onItemRemove={(_favoriteIndex, favorite) => {
                      if (isReadMode) {
                        return;
                      }
                      if (hasFavoriteAccess(favorite)) {
                        setFavoritesCatalog((current) => [...current, favorite]);
                      }
                      remove(getAllResources().findIndex((f) => f.code === favorite.code));
                    }}
                    canMoveItems={!searchValue && !isReadMode}
                    readMode={isReadMode}
                  />
                  <ListsSeparator />
                  <FavoritesCatalog
                    readMode={isReadMode}
                    catalogFavorites={filteredFavoritesCatalog || []}
                    onItemClick={(favorite) => {
                      if (isReadMode) {
                        return;
                      }
                      setFavoritesCatalog((current) =>
                        current.filter((item) => item.code !== favorite.code),
                      );
                      append(favorite);
                    }}
                  />
                </DxGrid>
              </DxContainer>
            </DxGrid>
          </DxCardContent>
        </DxCard>
        <DxButton
          type="text"
          style={{ marginBottom: '8px' }}
          data-testid="favorites-back"
          id="favorites-back"
          onClick={getContinuationHandler(navigatePrevious)}
          label={i18next.t('favorites.exitAndBack')}
          icon={checkIcon('pfeil-links')}
        />
      </DxContainer>
      <UnsavedOrPendingModal
        showPending={continuation !== undefined && isSubmitting}
        showUnsaved={continuation !== undefined && !isSubmitting}
        onCancel={continuation?.onCancel}
        onConfirm={continuation?.onConfirm}
      />
    </>
  );
};
